import type { VenueOSClient } from '@mappedin/venueos-applet';
import { AnalyticsClient } from '@venueos/analytics-sdk';
import { useEffect, useRef, useState } from 'react';

interface UseAnalyticsClientProps {
  venueOSClient: VenueOSClient | null;
}

interface UseAnalyticsClientReturn {
  analyticsClient: AnalyticsClient | null;
  isReady: boolean;
  error: string | null;
}

/**
 * Wrap the VenueOS applet client with the typed `AnalyticsClient`
 * from `@venueos/analytics-sdk`, mirroring the pattern used by
 * `apps/web-applet` and `apps/directory-applet`. The SDK proxies all
 * Snowflake reads through `/api/analytics/*` (which the host injects
 * `_appId`/`orgId` into), so the applet must hold the `analytics:read`
 * permission for the analytics-backed layers to succeed.
 */
export function useAnalyticsClient({
  venueOSClient,
}: UseAnalyticsClientProps): UseAnalyticsClientReturn {
  const [analyticsClient, setAnalyticsClient] = useState<AnalyticsClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<AnalyticsClient | null>(null);

  useEffect(() => {
    if (!venueOSClient) {
      if (clientRef.current) {
        clientRef.current.destroy();
        clientRef.current = null;
      }
      setAnalyticsClient(null);
      setIsReady(false);
      return;
    }

    try {
      const client = new AnalyticsClient(venueOSClient);
      clientRef.current = client;
      setAnalyticsClient(client);
      setIsReady(true);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to initialize analytics client');
      setAnalyticsClient(null);
      setIsReady(false);
    }

    return () => {
      if (clientRef.current) {
        clientRef.current.destroy();
        clientRef.current = null;
      }
    };
  }, [venueOSClient]);

  return { analyticsClient, isReady, error };
}
